/*
  The figures in a game page's header (Robert, 2026-10-20: "if there's any other information
  that should be added … that's quite cool, then let's try it"). Pure, so they are
  unit-tested; GameHeader only lays them out.
*/

import { isMintGameCode, resolveGameCategory } from "./categories";
import type { GameSummaryDto } from "types/v2/catalog";

export interface GameRank {
  /* 1-based place by cards graded among the live games in the same home category. */
  rank: number;
  of: number;
  /* "TCG" or "Sports": the category the rank is within. */
  category: string;
}

/*
  Where a game ranks by cards graded among the games the home page lists beside it: the same
  category, not archived, not New Zealand Mint (barred from every public surface, OVD16),
  with a usable count. Ties share the better place. Null when the game is not one of those,
  or has no count, so the header says nothing rather than a wrong number.
*/
export const gameRank = (games: GameSummaryDto[], gameCode: string): GameRank | null => {
  const code = gameCode.toLowerCase();
  const game = games.find((g) => g.gameCode.toLowerCase() === code);
  const category = game ? resolveGameCategory(game.category) : null;
  if (!game || !category || game.archived || typeof game.cardsGraded !== "number") {
    return null;
  }
  const peers = games.filter(
    (g) =>
      !g.archived &&
      !isMintGameCode(g.gameCode) &&
      resolveGameCategory(g.category) === category &&
      typeof g.cardsGraded === "number" &&
      isFinite(g.cardsGraded)
  );
  const mine = game.cardsGraded;
  const rank = 1 + peers.filter((g) => (g.cardsGraded as number) > mine).length;
  return { rank, of: peers.length, category };
};

/* "1st", "2nd", "3rd", "11th", "22nd". */
export const ordinal = (n: number): string => {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) {
    return `${n}th`;
  }
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
};
